import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { JobStatus } from 'common/BulkLoading';
import { trackError } from 'util/error';
import { pollRepeatedly } from 'api/shared';
import { generatePlantingPrescription } from './api';
import { queryClient } from 'common/providers/QueryProvider';
import {
  BulkGeneratePlantingPrescriptionInput,
  FieldGenerationStatus,
  GeneratePlantingPrescriptionMutationParams,
  PrescriptionGenerationJob,
  PrescriptionGenerationJobQueryResponse,
} from './types';
import {
  IN_PROGRESS_JOB_STATUS,
  JOB_STATUS_KEY,
  PRESCRIPTION_GENERATION_JOB_POLLING_INTERVAL,
} from './const';
import { RecommendationOptions } from '../../types';
import { convert, convertSeedingRateTo } from 'util/unitConversion';
import { UNIT_TYPES } from 'constants/unitTypes';
import { useAppSelector } from 'app/hooks';
import { getActiveCropNominalWeight } from 'current/currentSelectors';

interface UseBulkGeneratePlantingPrescriptionProps {
  onAllComplete?: (results: FieldGenerationStatus[]) => void;
  onFieldSuccess?: (fieldId: string, prescriptionId: string) => void;
  onFieldError?: (fieldId: string, errorMessage?: string, errorCode?: string) => void;
  setIsGeneratingPlantingPrescriptions?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useBulkGeneratePlantingPrescription = ({
  onAllComplete,
  onFieldSuccess,
  onFieldError,
  setIsGeneratingPlantingPrescriptions
}: UseBulkGeneratePlantingPrescriptionProps = {}) => {
  const [fieldStatuses, setFieldStatuses] = useState<FieldGenerationStatus[]>([]);
  const hasStartedRef = useRef(false);
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);

  const updateFieldStatus = useCallback((fieldId: string, updates: Partial<FieldGenerationStatus>) => {
    setFieldStatuses((prev) =>
      prev.map((field) =>
        field.fieldId === fieldId ? { ...field, ...updates } : field
      )
    );
  }, []);

  const { mutate } = useMutation<
    PrescriptionGenerationJob,
    Error,
    { fieldId: string; params: GeneratePlantingPrescriptionMutationParams }
  >({
    mutationFn: ({ fieldId, params }) => pollRepeatedly({
      callToPoll: () => generatePlantingPrescription(params).then((response) => {
        const job = response.generatePlantingPrescription;

        queryClient.setQueryData<PrescriptionGenerationJobQueryResponse>(
          [JOB_STATUS_KEY, fieldId],
          { prescriptionGenerationJob: job }
        );

        if (IN_PROGRESS_JOB_STATUS.includes(job.status)) {
          updateFieldStatus(fieldId, { status: job.status });
        }

        return job;
      }),
      pollUntil: ((job: PrescriptionGenerationJob) =>
        !IN_PROGRESS_JOB_STATUS.includes(job.status)) as unknown as () => boolean,
      parseInterval: () => PRESCRIPTION_GENERATION_JOB_POLLING_INTERVAL,
    }),
    onSuccess: (job, { fieldId }) => {
      if (job.status === JobStatus.SUCCEEDED && job.result) {
        updateFieldStatus(fieldId, { status: job.status, prescriptionId: job.id });
        onFieldSuccess?.(fieldId, job.id);
      } else {
        updateFieldStatus(fieldId, {
          status: JobStatus.FAILED,
          errorMessage: job.errorMessage,
          errorCode: job.errorCode,
        });
        onFieldError?.(fieldId, job.errorMessage, job.errorCode);
      }
    },
    onError: (error, { fieldId }) => {
      updateFieldStatus(fieldId, {
        status: JobStatus.FAILED,
        // TODO: Test this error message because it should return in english and cannot be translated,
        // so it will be removed
        errorMessage: error.message,
      });
      onFieldError?.(fieldId, error.message, error.name);
      trackError(new Error('Generate Planting Prescription Failed'), {
        fieldId,
        errorMessage: error.message,
      });
    },
  });

  useEffect(() => {
    if (fieldStatuses.length === 0 || !hasStartedRef.current) return;

    const allComplete = fieldStatuses.every(
      (f) => f.status === JobStatus.SUCCEEDED || f.status === JobStatus.FAILED
    );

    if (allComplete) {
      onAllComplete?.(fieldStatuses);
    }
  }, [fieldStatuses, onAllComplete]);

  const initBulkGeneration = useCallback(
    ({
      countryCode,
      fields,
      zoneGenerationInfo = { models: ['YIELD', 'CCI'] },
      numZones = 5,
      forceReprocess = false,
      season
    }: BulkGeneratePlantingPrescriptionInput) => {
      hasStartedRef.current = true;

      const initialStatuses: FieldGenerationStatus[] = fields
      .filter((field) => field.recommendation)
      .map((field, index) => ({
        id: index + 1,
        fieldId: field.id,
        label: field.name,
        status: JobStatus.PENDING,
      }));

      setIsGeneratingPlantingPrescriptions(true);
      setFieldStatuses(initialStatuses);

      fields.forEach((field) => {
        if (!field.recommendation) return;

        const targetSeedingRate = field.selectedRecommendation === RecommendationOptions.FVSS_RECOMMENDATION
          ? field.recommendation.recommendation
          : convertSeedingRateTo(field.avgFixedPopulation, field.recommendation.recommendation.u);

        const metricTargetYield = convert({
          from: { u: field.recommendation.targetYield.u, q: field.recommendation.targetYield.v },
          to: { u: UNIT_TYPES.kg_hectare },
          cropNominalWeight
        });

        mutate({
          fieldId: field.id,
          params: {
            target: {
              id: field.id,
              type: 'FIELD',
            },
            targetSeedingRate,
            targetYield: {
              v: metricTargetYield.q,
              u: metricTargetYield.u,
            },
            productId: field.hybrid?.uuid ?? '',
            zoneGenerationInfo,
            numZones,
            forceReprocess,
            season,
            countryCode
          },
        });
      });
    },
    [cropNominalWeight, mutate, setIsGeneratingPlantingPrescriptions]
  );

  const isLoading = useMemo(
    () => fieldStatuses.some((f) => IN_PROGRESS_JOB_STATUS.includes(f.status)),
    [fieldStatuses]
  );

  const isComplete = useMemo(
    () =>
      fieldStatuses.length > 0
      && fieldStatuses.every((f) => f.status === JobStatus.SUCCEEDED || f.status === JobStatus.FAILED),
    [fieldStatuses]
  );

  return {
    initBulkGeneration,
    fieldStatuses,
    isLoading,
    isComplete,
  };
};
